/**
 * AboutPage - public "About Us" page for NLT Radio
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mic2, Store, Users } from 'lucide-react';
import Footer from '../components/Footer';

const values = [
  { icon: Mic2, title: 'Artists Own the Story', text: 'No algorithm deciding who gets heard. You upload, you build, you own the audience.' },
  { icon: Users, title: 'Direct Connection', text: 'Talk to the people who actually support you — one fan, one stream, one show at a time.' },
  { icon: Store, title: 'You Are the Label', text: 'The artist is the label, the manager, and the movement — all in the palm of your hand.' },
];

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="about-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <span className="text-white font-semibold">About Us</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00F0FF]/10 via-transparent to-[#FF007F]/10 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-8 text-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            src="/nlt-radio-logo.png"
            alt="NLT Radio"
            className="w-28 h-28 mx-auto mb-6 object-contain drop-shadow-[0_0_35px_rgba(0,240,255,0.3)]"
          />
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Unbounded' }}
          >
            Real music deserves<br />a <span className="bg-gradient-to-r from-[#00F0FF] to-[#FF007F] bg-clip-text text-transparent">real home.</span>
          </motion.h1>
        </div>
      </div>

      {/* Story */}
      <div className="max-w-2xl mx-auto px-5 py-6 space-y-6">
        <p className="text-[#D4D4D8] text-base sm:text-lg leading-relaxed">
          NLT Radio was born from a simple belief: real music deserves a real home. Not another
          algorithm-driven feed drowning out independent voices, but a place where artists own their
          story, connect directly with the people who support them, and build something lasting —
          one fan, one stream, one show at a time.
        </p>
        <p className="text-[#D4D4D8] text-base sm:text-lg leading-relaxed">
          We're bringing back the grind. The kind of hustle that built hip hop and independent music
          from the ground up — artists getting out there, connecting, creating, and owning their
          audience instead of feeding a machine that doesn't know their name. NLT Radio gives that
          hustle a digital home: a place to upload your music, build your following, sell directly to
          your fans, and grow on your own terms.
        </p>
        <p className="text-[#D4D4D8] text-base sm:text-lg leading-relaxed">
          This isn't about competing with the majors. It's about proving there's another way — one
          where the artist is the label, the manager, and the movement, all in the palm of their hand.
        </p>
      </div>

      {/* Values */}
      <div className="max-w-4xl mx-auto px-5 py-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl bg-white/5 border border-white/10 p-5"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1.5">{v.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{v.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-2xl mx-auto px-5 py-8 text-center">
        <button
          onClick={() => navigate('/roadmap')}
          className="px-6 py-3 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-white font-semibold hover:opacity-90 transition"
          data-testid="about-view-roadmap-btn"
        >
          See where we're headed →
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
